import { useState } from 'react';
import { useChild } from '../../contexts/ChildContext';
import ChildSelector from './ChildSelector';

/**
 * ChildSwitcherModal — a compact pill button in the top bar that opens
 * a modal containing the full ChildSelector.
 *
 * Props (all optional):
 *   forceOpen     — when true the modal is forced open (e.g. during a tutorial step)
 *   onForceClose  — called when the user closes the modal while forceOpen is active
 */
export default function ChildSwitcherModal({ forceOpen = false, onForceClose }) {
  const { selectedChild } = useChild();
  const [internalOpen, setInternalOpen] = useState(false);

  const open = internalOpen || forceOpen;

  const handleClose = () => {
    setInternalOpen(false);
    if (forceOpen) onForceClose?.();
  };

  const genderEmoji = selectedChild
    ? selectedChild.gender === 'male' ? '👦' : selectedChild.gender === 'female' ? '👧' : '👶'
    : '👶';

  return (
    <>
      {/* Pill trigger */}
      <button
        className="child-switcher-pill"
        onClick={() => setInternalOpen(true)}
        aria-label={selectedChild ? `Switch child — currently ${selectedChild.name}` : 'Add a child'}
        title={selectedChild ? 'Switch or manage children' : 'Add a child'}
      >
        <span className="child-switcher-emoji" aria-hidden="true">{genderEmoji}</span>
        <span className="child-switcher-name">
          {selectedChild ? selectedChild.name : 'Add child'}
        </span>
        <span className="child-switcher-caret" aria-hidden="true">▾</span>
      </button>

      {/* Modal */}
      {open && (
        <div
          className="child-switcher-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
          role="dialog"
          aria-modal="true"
          aria-label="Switch child"
        >
          <div className="child-switcher-modal">
            <div className="child-switcher-modal-header">
              <span className="child-switcher-modal-title">Children</span>
              <button
                className="child-switcher-modal-close"
                onClick={handleClose}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="child-switcher-modal-body">
              <ChildSelector onChildSelected={handleClose} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
